import React from 'react';
import { cva } from 'class-variance-authority';
import { cn } from '../../lib/utils';

/**
 * IconButton variants using CVA
 */
export const iconButtonVariants = cva(
  'inline-flex items-center justify-center rounded-lg transition-all duration-normal focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default:
          'text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-white',
        primary:
          'text-primary-600 hover:bg-primary-50 dark:text-primary-400 dark:hover:bg-primary-900/20',
        secondary:
          'text-accent-600 hover:bg-accent-50 dark:text-accent-400 dark:hover:bg-accent-900/20',
        danger:
          'text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20',
        ghost:
          'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200',
      },
      size: {
        xs: 'h-6 w-6',
        sm: 'h-8 w-8',
        md: 'h-10 w-10',
        lg: 'h-12 w-12',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'md',
    },
  }
);

/**
 * IconButton Component
 *
 * @param {Object} props
 * @param {'default'|'primary'|'secondary'|'danger'|'ghost'} [props.variant='default']
 * @param {'xs'|'sm'|'md'|'lg'} [props.size='md']
 * @param {string} [props.className]
 * @param {string} [props.ariaLabel]
 * @param {React.ReactNode} props.children
 */
const IconButton = React.forwardRef(({
  className,
  variant,
  size,
  ariaLabel,
  children,
  ...props
}, ref) => {
  return (
    <button
      ref={ref}
      className={cn(iconButtonVariants({ variant, size }), className)}
      aria-label={ariaLabel}
      {...props}
    >
      {children}
    </button>
  );
});

IconButton.displayName = 'IconButton';

export { IconButton };
export default IconButton;
